import { Injectable } from '@angular/core'
import { ListJsonResponse, PageDetails, PagedJsonResponse } from '@ftr/contracts/api/core'
import {
  ConfigureLocationBody,
  ConfigureLocationQuickDraftSttBody,
  ConfigureRealTimeHearingsBody,
  CreateLocation,
  ListLocationsQuery,
  Location,
  UpdateLocation,
} from '@ftr/contracts/api/location'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, collectData, failUndefinedData, mapData, recoverType, switchMapData } from '@ftr/foundation'
import { Observable } from 'rxjs'
import { EntityResponse, JsonResponseMapper } from '~app/core/http/json-response.mapper'
import { ApiClient } from '~app/services/ftr-api/api-client'
import { ApiClientFactory } from '~app/services/ftr-api/api-client.factory'
import { NotFoundApiError } from '~app/services/ftr-api/not-found-api.error'
import { PagedResult } from '~app/shared/PagedResult'
import { serializeHttpParams } from '~app/util/serializeHttpParams'
import { ChildLocation } from './ChildLocation'
import { RootLocation } from './RootLocation'

const PAGE_SIZE = 100

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  lastCreatedCourtHouse?: Location

  private readonly apiClient: ApiClient

  constructor(
    apiClientFactory: ApiClientFactory,
    private readonly jsonResponseMapper: JsonResponseMapper,
  ) {
    this.apiClient = apiClientFactory.build('location')
  }

  /**
   * @param courtSystemId
   * @param isArchived Return locations based on their isArchived status
   * @param filterInaccessible Hide locations the user doesn't have access to when restricted permissions are enabled
   */
  getAllRootsForCourtSystem(
    courtSystemId: Uuid,
    isArchived: boolean | undefined = false,
    filterInaccessible = false,
  ): ApiResult<RootLocation[]> {
    const remote = this.apiClient.get<ListJsonResponse<RootLocation>>({
      params: serializeHttpParams(
        new ListLocationsQuery(courtSystemId, isArchived, undefined, undefined, filterInaccessible),
      ),
    })
    return remote.pipe(mapData(response => mapRootsResponse(response)))
  }

  /**
   * @param courtSystemId
   * @param page
   * @param isArchived Return locations based on their isArchived status
   * @param filterInaccessible Hide locations the user doesn't have access to when restricted permissions are enabled
   */
  getRootsForCourtSystem(
    courtSystemId: Uuid,
    page: number,
    isArchived?: boolean | undefined,
    filterInaccessible?: boolean | undefined,
  ): ApiResult<PagedResult<RootLocation>> {
    return this.apiClient
      .get<PagedJsonResponse<RootLocation>>({
        params: serializeHttpParams(
          new ListLocationsQuery(
            courtSystemId,
            isArchived,
            new PageDetails(page, PAGE_SIZE),
            undefined,
            filterInaccessible,
          ),
        ),
      })
      .pipe(mapData(response => mapPagedRootsResponse(response)))
  }

  /**
   * Like get, except it populates parent information
   */
  getCourtroom(id: Uuid): ApiResult<Location | undefined> {
    return this.get(id).pipe(
      switchMapData(courtroom => {
        if (!courtroom) {
          return ApiResult.success(undefined)
        }
        if (!courtroom?.parentId) {
          return ApiResult.failure(new Error(`${id} is not a courtroom`))
        }
        return this.get(courtroom.parentId).pipe(
          mapData(courthouse => {
            courtroom.parent = courthouse
            return courtroom
          }),
        )
      }),
    )
  }

  getCourtroomOrError(id: Uuid): ApiResult<Location> {
    return this.getCourtroom(id).pipe(failUndefinedData(() => new Error(`Courtroom not found: ${id}`)))
  }

  get(id: Uuid): ApiResult<Location | undefined> {
    const remote = this.apiClient.get<EntityResponse<Location>>({ path: id })
    return remote.pipe(
      mapData(response => this.jsonResponseMapper.mapEntity(response)),
      recoverType(NotFoundApiError, undefined),
    )
  }

  /**
   * @deprecated Use get and/or getCourtroom instead
   */
  getLocation(id: Uuid): Observable<Location> {
    const remote = this.apiClient.get<EntityResponse<Location>>({ path: id })
    return remote.pipe(collectData(response => this.jsonResponseMapper.mapEntity(response)))
  }

  createLocation(location: CreateLocation): ApiResult<Location> {
    return this.apiClient.post({ body: location })
  }

  updateLocation(id: string, location: UpdateLocation): ApiResult<Location> {
    return this.apiClient.put({ path: id, body: location })
  }

  configureLocation(id: string, location: ConfigureLocationBody): ApiResult<Location> {
    return this.apiClient.put({ path: `${id}/configure`, body: location })
  }

  configureLocationQuickDraftStt(id: string, configuration: ConfigureLocationQuickDraftSttBody): ApiResult<Location> {
    return this.apiClient.put({ path: `${id}/configureQuickDraftStt`, body: configuration })
  }

  configureRealTimeHearings(id: string, configuration: ConfigureRealTimeHearingsBody): ApiResult<Location> {
    return this.apiClient.put({ path: `${id}/configureRealTimeHearings`, body: configuration })
  }
}

function mapPagedRootsResponse(response: PagedJsonResponse<RootLocation>): PagedResult<RootLocation> {
  return {
    meta: response.meta,
    items: mapRootsResponse(response),
  }
}

export function mapRootsResponse(response: ListJsonResponse<RootLocation>): RootLocation[] {
  return response.data!.map(item => mapRoot(item.attributes))
}

function mapRoot(location: Location): RootLocation {
  return {
    ...location,
    parentId: undefined,
    parent: undefined,
    children: (location.children || []).map(child => mapChild(location as RootLocation, child)),
  }
}

function mapChild(root: RootLocation, child: Location): ChildLocation {
  return {
    ...child,
    parentId: root.id,
    parent: root,
    children: undefined,
  }
}
